/**
 * Iter 253 · AI Duty Wizard dialog (Claude-powered).
 *
 * Two-step wizard:
 *   1. HR fills designation + department + industry + optional context, hits
 *      "Suggest".
 *   2. Grid renders duties with per-row accept checkboxes (default: all on).
 *      HR can tweak weightages inline, drop rows, refine via a follow-up
 *      prompt, or Apply.
 *
 * Apply mode:
 *   - designationId present → merge/replace onto that designation.
 *   - designationId null    → creates a new designation using designation_name.
 */
import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Sparkles, Loader2, Wand2, X, RefreshCw, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { apiErrorMessage } from "@/lib/apiError";

const FREQ_OPTS = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "bi_weekly", label: "Bi-Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "bi_monthly", label: "Bimonthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "half_yearly", label: "Half Yearly" },
  { value: "yearly", label: "Yearly" },
  { value: "one_time", label: "One-time" },
];

export default function AIDutyWizardDialog({
  open,
  onOpenChange,
  designationId = null,   // null → creates new
  seedName = "",
  seedDepartment = "",
  existingDutiesCount = 0,
  onApplied,              // (designation) => void — parent refresh hook
}) {
  const { activeWorkspaceId } = useAuth();
  const [step, setStep] = useState("input"); // input | review
  const [name, setName] = useState(seedName);
  const [department, setDepartment] = useState(seedDepartment);
  const [industry, setIndustry] = useState("");
  const [extra, setExtra] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [rationale, setRationale] = useState("");
  const [checked, setChecked] = useState({}); // idx → bool
  const [feedback, setFeedback] = useState("");
  const [mode, setMode] = useState(existingDutiesCount > 0 ? "append" : "replace");
  const [busy, setBusy] = useState(false);

  // Iter 253 · Sync internal state with props each time the dialog opens.
  // Fixes the "Extend wizard shows empty name" bug where useState() only
  // captured props on first mount.
  useEffect(() => {
    if (!open) return;
    setName(seedName || "");
    setDepartment(seedDepartment || "");
    setIndustry("");
    setExtra("");
    setStep("input");
    setSuggestions([]);
    setSessionId(null);
    setRationale("");
    setChecked({});
    setFeedback("");
    setMode(existingDutiesCount > 0 ? "append" : "replace");
  }, [open]);

  const reset = () => {
    setStep("input");
    setSuggestions([]);
    setSessionId(null);
    setRationale("");
    setChecked({});
    setFeedback("");
  };

  const closeDialog = (open) => {
    onOpenChange(open);
    if (!open) reset();
  };

  const suggest = async () => {
    if (!name.trim()) {
      toast.error("Designation name is required");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(
        `/workspaces/${activeWorkspaceId}/hr/designations/wizard/suggest`,
        {
          designation_name: name.trim(),
          department: department || null,
          industry: industry || null,
          extra_context: extra || null,
        },
      );
      setSuggestions(data.duties || []);
      setSessionId(data.session_id || null);
      setRationale(data.rationale || "");
      // default: everything checked
      const initChecked = {};
      (data.duties || []).forEach((_, i) => (initChecked[i] = true));
      setChecked(initChecked);
      setStep("review");
      if (!(data.duties || []).length) {
        toast.warning("Wizard returned no duties. Try adding more context.");
      }
    } catch (e) {
      toast.error(apiErrorMessage(e, "Wizard failed"));
    } finally {
      setBusy(false);
    }
  };

  // Iter 253d · Start refinement from the designation's existing duties.
  // Skips the initial LLM call — user can then use the refine input to
  // add / consolidate / re-weight with a natural-language prompt.
  const startFromExisting = async () => {
    if (!designationId) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/hr/designations/${designationId}`);
      const existing = (data.default_duties || []).map((d) => ({
        title: d.title || "",
        description: d.description || "",
        frequency: d.frequency || "monthly",
        weightage: d.weightage || 0,
      }));
      if (!existing.length) {
        toast.warning("Designation has no duties yet — use Suggest instead.");
        setBusy(false);
        return;
      }
      setSuggestions(existing);
      setSessionId(null);
      setRationale("Loaded existing duties — describe what you'd like to change below and click Refine.");
      const initChecked = {};
      existing.forEach((_, i) => (initChecked[i] = true));
      setChecked(initChecked);
      setStep("review");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load duties"));
    } finally { setBusy(false); }
  };

  const refine = async () => {
    if (!feedback.trim()) {
      toast.error("Type a refinement instruction first");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(
        `/workspaces/${activeWorkspaceId}/hr/designations/wizard/refine`,
        {
          session_id: sessionId,
          current_suggestions: suggestions,
          designation_name: name.trim(),
          department: department || null,
          industry: industry || null,
          feedback: feedback.trim(),
        },
      );
      setSuggestions(data.duties || []);
      setRationale(data.rationale || rationale);
      const initChecked = {};
      (data.duties || []).forEach((_, i) => (initChecked[i] = true));
      setChecked(initChecked);
      setFeedback("");
      toast.success("Suggestions refined");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Refine failed"));
    } finally {
      setBusy(false);
    }
  };

  const patchSuggestion = (idx, patch) =>
    setSuggestions((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));

  const removeSuggestion = (idx) => {
    setSuggestions((prev) => prev.filter((_, i) => i !== idx));
    setChecked((prev) => {
      const next = {};
      let dst = 0;
      Object.keys(prev).forEach((k) => {
        const kk = parseInt(k, 10);
        if (kk === idx) return;
        next[dst++] = prev[k];
      });
      return next;
    });
  };

  const acceptedList = () =>
    suggestions.filter((_, i) => checked[i]).map((d) => ({
      title: d.title,
      description: d.description,
      frequency: d.frequency,
      weightage: parseFloat(d.weightage) || 0,
    }));

  const totalWeightage = acceptedList().reduce((s, d) => s + (d.weightage || 0), 0);

  const apply = async () => {
    const accepted = acceptedList();
    if (!accepted.length) {
      toast.error("Check at least one duty to apply");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(
        `/workspaces/${activeWorkspaceId}/hr/designations/wizard/apply`,
        {
          designation_id: designationId,
          designation_name: designationId ? null : name.trim(),
          department: designationId ? null : department || null,
          accepted_duties: accepted,
          mode,
        },
      );
      toast.success(designationId ? `${mode === "replace" ? "Replaced" : "Merged"} ${accepted.length} duties` : `Created "${data.name}"`);
      onApplied && onApplied(data);
      closeDialog(false);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Apply failed"));
    } finally {
      setBusy(false);
    }
  };

  const totalColor = Math.abs(totalWeightage - 100) < 0.5 ? "text-emerald-600" : "text-rose-600";

  return (
    <Dialog open={open} onOpenChange={closeDialog}>
      <DialogContent className="max-w-3xl" data-testid="ai-duty-wizard-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            AI Duty Wizard
          </DialogTitle>
          <DialogDescription>
            Claude suggests industry-standard job duties with weightages that total 100%. Review, tweak, and apply to a designation.
          </DialogDescription>
        </DialogHeader>

        {step === "input" && (
          <div className="space-y-3">
            <div>
              <div className="label-eyebrow mb-1">Designation name*</div>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Senior Software Engineer"
                data-testid="wizard-name-input"
                disabled={!!designationId}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="label-eyebrow mb-1">Department</div>
                <Input
                  value={department}
                  onChange={(e) => setDepartment(e.target.value)}
                  placeholder="e.g. Engineering"
                  data-testid="wizard-department-input"
                />
              </div>
              <div>
                <div className="label-eyebrow mb-1">Industry (helps context)</div>
                <Input
                  value={industry}
                  onChange={(e) => setIndustry(e.target.value)}
                  placeholder="e.g. SaaS, Fintech, Ad agency"
                  data-testid="wizard-industry-input"
                />
              </div>
            </div>
            <div>
              <div className="label-eyebrow mb-1">Extra context (optional)</div>
              <Textarea
                value={extra}
                onChange={(e) => setExtra(e.target.value)}
                placeholder="Any nuance you'd like Claude to weigh — e.g. 'reports into VP Product, owns the mobile stack, 4-person team'"
                rows={3}
                data-testid="wizard-extra-input"
              />
            </div>
            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => closeDialog(false)}
                className="px-3 py-2 border border-border hover:bg-muted text-sm"
                data-testid="wizard-cancel-btn"
              >
                Cancel
              </button>
              {designationId && existingDutiesCount > 0 && (
                <button
                  type="button"
                  onClick={startFromExisting}
                  disabled={busy}
                  className="px-3 py-2 border border-purple-500 text-purple-600 hover:bg-purple-50 text-sm disabled:opacity-50 flex items-center gap-1.5"
                  data-testid="wizard-start-from-existing-btn"
                  title="Load the designation's current duties into the review grid — then use the Refine box to modify them with a prompt."
                >
                  <Wand2 className="w-4 h-4" /> Refine existing duties
                </button>
              )}
              <button
                type="button"
                onClick={suggest}
                disabled={busy || !name.trim()}
                className="px-3 py-2 border border-primary bg-primary text-primary-foreground text-sm hover:bg-primary/90 disabled:opacity-50 flex items-center gap-1.5"
                data-testid="wizard-suggest-btn"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                {busy ? "Thinking…" : "Suggest fresh duties"}
              </button>
            </div>
          </div>
        )}

        {step === "review" && (
          <div className="space-y-3">
            {rationale && (
              <div className="text-xs text-muted-foreground italic border-l-2 border-primary pl-2" data-testid="wizard-rationale">
                {rationale}
              </div>
            )}
            <div className="flex items-center justify-between text-xs">
              <div>
                <b>{suggestions.length}</b> duties suggested · <b>{Object.values(checked).filter(Boolean).length}</b> accepted ·
                <span className={`ml-1 font-bold ${totalColor}`} data-testid="wizard-accepted-weightage-total">
                  Σ = {totalWeightage.toFixed(1)}%
                </span>
              </div>
              {designationId && existingDutiesCount > 0 && (
                <div className="flex items-center gap-1">
                  <span className="text-muted-foreground">Apply mode:</span>
                  <button
                    type="button"
                    onClick={() => setMode("append")}
                    className={`px-2 py-0.5 border ${mode === "append" ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}
                    data-testid="wizard-mode-append"
                  >
                    Append
                  </button>
                  <button
                    type="button"
                    onClick={() => setMode("replace")}
                    className={`px-2 py-0.5 border ${mode === "replace" ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}
                    data-testid="wizard-mode-replace"
                  >
                    Replace all
                  </button>
                </div>
              )}
            </div>

            <div className="border border-border max-h-[45vh] overflow-y-auto" data-testid="wizard-suggestion-table">
              <div className="grid grid-cols-[30px_1fr_120px_80px_30px] gap-1 px-2 py-1.5 bg-muted/30 text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border sticky top-0 z-10">
                <div></div>
                <div>Duty</div>
                <div>Frequency</div>
                <div>Weight %</div>
                <div></div>
              </div>
              {suggestions.map((s, i) => (
                <div
                  key={i}
                  className={`grid grid-cols-[30px_1fr_120px_80px_30px] gap-1 px-2 py-2 border-b border-border last:border-b-0 items-start ${checked[i] ? "" : "opacity-40"}`}
                  data-testid={`wizard-suggestion-row-${i}`}
                >
                  <input
                    type="checkbox"
                    checked={!!checked[i]}
                    onChange={(e) => setChecked((prev) => ({ ...prev, [i]: e.target.checked }))}
                    className="mt-1"
                    data-testid={`wizard-suggestion-check-${i}`}
                  />
                  <div className="space-y-1">
                    <Input
                      value={s.title}
                      onChange={(e) => patchSuggestion(i, { title: e.target.value })}
                      className="h-7 text-xs font-medium"
                      data-testid={`wizard-suggestion-title-${i}`}
                    />
                    {s.description && (
                      <div
                        className="text-[11px] text-muted-foreground line-clamp-3 prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{ __html: s.description }}
                      />
                    )}
                  </div>
                  <Select value={s.frequency} onValueChange={(v) => patchSuggestion(i, { frequency: v })}>
                    <SelectTrigger className="h-7 text-xs" data-testid={`wizard-suggestion-freq-${i}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FREQ_OPTS.map((f) => (
                        <SelectItem key={f.value} value={f.value}>
                          {f.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.5"
                    value={s.weightage}
                    onChange={(e) => patchSuggestion(i, { weightage: e.target.value })}
                    className="h-7 text-xs text-right"
                    data-testid={`wizard-suggestion-weight-${i}`}
                  />
                  <button
                    type="button"
                    onClick={() => removeSuggestion(i)}
                    className="text-muted-foreground hover:text-rose-600 p-0.5 mt-0.5"
                    title="Remove suggestion"
                    data-testid={`wizard-suggestion-remove-${i}`}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              {suggestions.length === 0 && (
                <div className="p-4 text-xs text-muted-foreground text-center">
                  No suggestions. Try refining.
                </div>
              )}
            </div>

            <div className="border border-border p-2 space-y-2 bg-muted/10">
              <div className="label-eyebrow flex items-center gap-1">
                <RefreshCw className="w-3 h-3" /> Refine
              </div>
              <div className="flex gap-2">
                <Input
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder="e.g. 'Fewer duties, focus on client-facing work'"
                  className="h-8 text-xs"
                  data-testid="wizard-refine-input"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") refine();
                  }}
                />
                <button
                  type="button"
                  onClick={refine}
                  disabled={busy || !feedback.trim()}
                  className="px-3 py-1 border border-border text-xs hover:bg-muted disabled:opacity-50 flex items-center gap-1"
                  data-testid="wizard-refine-btn"
                >
                  {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
                  Refine
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between pt-2">
              <button
                type="button"
                onClick={() => setStep("input")}
                className="px-3 py-2 border border-border hover:bg-muted text-sm"
                data-testid="wizard-back-btn"
              >
                ← Back
              </button>
              <button
                type="button"
                onClick={apply}
                disabled={busy || acceptedList().length === 0}
                className="px-3 py-2 border border-emerald-500 bg-emerald-500 text-white text-sm hover:bg-emerald-600 disabled:opacity-50 flex items-center gap-1.5"
                data-testid="wizard-apply-btn"
              >
                <Check className="w-4 h-4" />
                Apply {acceptedList().length} duties
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
